/**
 * Key dates (docs/02 §Key dates; docs/11 F-3; Matt 2026-08-25).
 *
 * Matt: "basically something that says 'This needs to be done on this date, and
 * I need a reminder to do it.'" A due date, what needs doing, and a way to close
 * it with a note — attached to whichever record the obligation actually belongs
 * to, and surfacing at the property either way because that is where the person
 * who has to act on it works.
 *
 * He asked whether "flag date" was the right term. "Key date" is what v1's own
 * engine produced (docs/11 F-3) and what lease administration calls them, so
 * the app and the industry agree on one word.
 */

import { z } from 'zod';

/**
 * What a key date can hang off — the same vocabulary `document_associations`
 * uses, because "what can a thing be attached to" should have one answer.
 *
 * Matt named lease, tenant, property, vendor, service agreement and utility
 * account. The rest come free from the shared list and cost nothing to allow.
 */
export const KEY_DATE_ENTITY_TYPES = [
  'property',
  'space',
  'property_group',
  'tenant_org',
  'tenancy',
  'lease',
  'vendor',
  'vendor_contact',
  'agreement',
  'utility_account',
  'management_agreement',
] as const;
export type KeyDateEntityType = (typeof KEY_DATE_ENTITY_TYPES)[number];

export const KEY_DATE_STATUSES = ['open', 'done', 'cancelled'] as const;
export type KeyDateStatus = (typeof KEY_DATE_STATUSES)[number];

/**
 * How far ahead an OPEN key date starts appearing in the Due soon lane.
 *
 * Longer than a certificate's 30 and shorter than a lease's 180, because a key
 * date is something a person has to schedule: two months is enough notice to
 * book a contractor and not so much that the lane fills with things nobody can
 * act on yet. Lives beside the other windows in `EXPIRATION_WINDOW_DAYS`.
 */
export const KEY_DATE_WINDOW_DAYS = 60;

export interface KeyDateRow {
  readonly id: string;
  readonly entityType: KeyDateEntityType;
  readonly entityId: string;
  /** What the record is called, so a list of key dates reads without lookups. */
  readonly entityLabel: string | null;
  readonly title: string;
  readonly detail: string | null;
  /**
   * NULL on a DERIVED item (0057): a row carries either a stored `due_on` that
   * somebody typed or a `source_key` naming the record whose date it tracks, and
   * the schema's CHECK makes that exclusive. It was typed `string` here, which
   * was a lie the moment 0057 dropped the NOT NULL — and a lie the screens
   * believed, rendering "NaN days ago" (docs/11 F-24).
   */
  readonly dueOn: string | null;
  readonly status: KeyDateStatus;
  /**
   * Everyone currently on it (0047). Empty means unclaimed — which is a real
   * state the UI says out loud, not a blank.
   *
   * Replaced a single `assignedTo` column that the UI could READ and never
   * write: Matt, 2026-08-25, "there actually doesnt appear to be anywhere users
   * can assign or add others to key dates right now or see who all is actually
   * part of the keydate task."
   */
  readonly assignees: readonly KeyDateAssignee[];
  readonly completedAt: string | null;
  readonly completedByName: string | null;
  readonly completionNote: string | null;
  /** How many progress notes have been written on it (docs/02 §Notes). */
  readonly noteCount: number;
  readonly version: number;
}

export const keyDateWriteSchema = z.object({
  entityType: z.enum(KEY_DATE_ENTITY_TYPES),
  entityId: z.uuid(),
  title: z.string().trim().min(1).max(200),
  detail: z.string().trim().max(4000).nullish(),
  dueOn: z.iso.date(),
  /**
   * Optional, and a LIST: "somebody on the team" is a real answer at creation
   * time, and so is "me and Rob". Empty or omitted creates it unclaimed.
   */
  assignees: z.array(z.uuid()).max(20).optional(),
});
export type KeyDateWrite = z.infer<typeof keyDateWriteSchema>;

/**
 * Editing an open key date: the date and the words.
 *
 * Ownership is deliberately NOT here. It has its own endpoints because it has
 * its own permissions (ADR-51: claim and invite ride `tasks.manage`, reassign
 * needs `tasks.assign`) and its own audit events. Folding it into a general
 * PATCH would mean one payload spanning two permission levels, and the narrower
 * one would end up unenforced.
 */
export const keyDateUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: z.strictObject({
    title: z.string().trim().min(1).max(200).optional(),
    detail: z.string().trim().max(4000).nullish(),
    dueOn: z.iso.date().optional(),
  }),
});

/**
 * Closing one. Matt: "when they close the action item, they can enter a
 * completion note."
 *
 * The note is REQUIRED on done and refused on cancelled: "what was done" is the
 * whole value of closing, while a cancelled date was never done and a note
 * claiming otherwise would be worse than none. Cancelling takes its reason
 * through `detail` on the way past, in the audit entry.
 */
export const keyDateCloseSchema = z.object({
  version: z.number().int().positive(),
  status: z.enum(['done', 'cancelled']),
  completionNote: z.string().trim().max(4000).nullish(),
});

/**
 * Why an owner stopped owning something (0047).
 *
 * Four of these are not decisions anybody made — they are consequences of a
 * person's account changing. Matt asked for exactly this: "what happens if a
 * user has something assigned to them and are removed from the property, their
 * role changes or the user is deactivated (i.e. terminated employee) so that we
 * do not loose dates." Each gets its OWN reason rather than a shared
 * "released", because "why is this unowned" is the question the admin queue
 * exists to answer and "released" does not answer it.
 */
export const KEY_DATE_RELEASE_REASONS = [
  /** They took themselves off. */
  'left',
  /** Somebody holding `tasks.assign` took them off (ADR-51). */
  'reassigned',
  /** The account was deactivated — the terminated-employee case. */
  'user_deactivated',
  /** They lost access to the property this item belongs to. */
  'lost_property_access',
  /** Their role changed and no longer carries `tasks.manage`. */
  'lost_permission',
  /** The item itself was closed; its owners close out with it. */
  'record_closed',
] as const;
export type KeyDateReleaseReason = (typeof KEY_DATE_RELEASE_REASONS)[number];

/** One person on an action item. Released rows are history, not owners. */
export interface KeyDateAssignee {
  readonly id: string;
  readonly userId: string;
  readonly userName: string | null;
  /**
   * Who put them on it — equal to `userId` for a self-claim, which is how the
   * UI tells "Dana claimed this" from "Rob asked Dana to help".
   */
  readonly addedBy: string;
  readonly addedByName: string | null;
  readonly addedAt: string;
  readonly releasedAt: string | null;
  readonly releasedByName: string | null;
  readonly releasedReason: KeyDateReleaseReason | null;
}

/**
 * Adding somebody. `userId` omitted means CLAIM (yourself) — the common case,
 * and the one a technician does without needing anyone's permission. Naming
 * somebody else is a JOIN/INVITE: also additive, also `tasks.manage`, because
 * asking for help is not directing another person's day (ADR-51).
 */
export const keyDateAssignSchema = z.object({
  userId: z.uuid().optional(),
});

/**
 * Removing somebody. Yourself is `left` and needs only `tasks.manage`; anybody
 * else is `reassigned` and needs `tasks.assign`, which the DATABASE enforces
 * (0047) rather than only the service.
 */
export const keyDateReleaseSchema = z.object({
  userId: z.uuid(),
});

export const keyDatesQuerySchema = z.object({
  entityType: z.enum(KEY_DATE_ENTITY_TYPES).optional(),
  entityId: z.uuid().optional(),
  /**
   * ROLL-UP: every key date whose record is governed by this property, whatever
   * it hangs off. Matt: a date on a tenant's lease "would also be a part of the
   * property as well since that is a call to action date for the property
   * manager, maintenance tech, maintenance manager".
   *
   * Resolved by `app_association_properties`, the one answer to "which
   * properties govern this record" — so the roll-up cannot drift from document
   * scope or note scope. A property's own key dates come back too, because that
   * function answers `('property', id)` with `[id]`.
   */
  propertyId: z.uuid().optional(),
  /**
   * `open` by default: a record page asks what is outstanding, not its history.
   *
   * `closed` is done OR cancelled — the ARCHIVE (punch item 2.3). Matt's words
   * were "inactive = completed", and 0057 already maps both states onto the
   * search index's `inactive`, so one value for "no longer outstanding" keeps
   * the archive and the search toggle describing the same set. `all` still
   * includes open, which is a different question.
   */
  status: z.enum(['open', 'done', 'cancelled', 'closed', 'all']).default('open'),
  /**
   * Due-date window, inclusive. Two separate bounds rather than a named range
   * ("this week", "overdue") because a property manager chasing a specific
   * month should not have to find the preset that happens to match it — and a
   * preset that drifts with today's date makes a shared link mean something
   * different tomorrow.
   *
   * An item with NO due date (0057's derived overlay carries `source_key`
   * instead) is excluded once either bound is set: it has no date to be inside
   * the window, and silently including it would make the filter a lie.
   */
  dueFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  dueTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  /**
   * Who is on it: a user id, `me`, or `unassigned`.
   *
   * `unassigned` is the one that earns its place — an action item nobody has
   * claimed is the one most likely to be missed, and before this there was no
   * way to ask for exactly those.
   */
  assignee: z.union([z.literal('me'), z.literal('unassigned'), z.uuid()]).optional(),
  /**
   * When it was CLOSED, inclusive — the archive's date question (punch 2.3).
   *
   * A separate window from `dueFrom`/`dueTo` because they answer different
   * things and a closed item usually answers both differently: "what did we
   * finish last month" is not "what was due last month", and an item finished
   * three weeks late sits in one window and not the other. Compared as calendar
   * dates against `completed_at`, so a reader picking "the 1st" gets the whole
   * of the 1st in their own reading of it.
   */
  closedFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  closedTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  /**
   * "Closed by me". Deliberately not `assignee`: closing RELEASES the owners
   * (0045's `app_release_key_date_owners`), so on a finished item "who had it"
   * is history and "who finished it" is the fact.
   *
   * `me` and nothing else, on purpose. A picker of everyone who has ever closed
   * an action item is a facet that grows without bound and cannot be derived
   * from a page (docs/03 §3a); "what did Rob finish in March" is a question the
   * audit ledger already answers, by actor. Accepting a uuid here would be a
   * capability with no surface to reach it — the shape docs/03 §5a names.
   */
  closedBy: z.literal('me').optional(),
  /**
   * Free text over the title, the detail and the COMPLETION NOTE.
   *
   * A filter, not the search index: global search finds a record from anywhere
   * (0051, and 0057 made action items its fifteenth kind); this narrows a list
   * you are already reading, the way every other list screen's `q` does. The
   * completion note is included because "what did we end up doing about the
   * backflow test" is the archive's whole reason to exist.
   */
  q: z.string().trim().max(200).optional(),
  /**
   * How the list reads. `worklist` is open-first, soonest-first — right for
   * something you are about to act on. `recent` is most-recently-CLOSED first,
   * which is the only sensible order for an archive: a list of finished work
   * sorted by a due date nobody will meet again is a list nobody can scan.
   */
  order: z.enum(['worklist', 'recent']).default('worklist'),
  /**
   * Ask for the SCOPE FACTS as well as the page (punch item 2.2): the tile
   * counts, and which kinds of record the scope actually holds.
   *
   * OFF by default because the record cards show neither and should not pay for
   * them: every property, tenancy, vendor and agreement page runs this list, and
   * an aggregate nobody renders is work done for nothing.
   */
  counts: z.enum(['off', 'on']).default('off'),
  /** Page size. Kept as `limit` because the record cards already ask for 5. */
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /** 1-based page over that size, for the table-first screens (punch item 2.2). */
  page: z.coerce.number().int().min(1).max(2000).default(1),
});
export type KeyDatesQuery = z.infer<typeof keyDatesQuerySchema>;

/**
 * The tile counts behind the action-items strip (punch item 2.2, from 2.1's
 * X-6.5: the counts come from ONE server-side query under the caller's own RLS).
 *
 * Computed over the RECORD SCOPE only — the entity or the property roll-up — and
 * deliberately NOT over the current filters. A tile is how you choose a filter,
 * so a count that moved when you chose one would be measuring the thing you were
 * about to change: pick Overdue and every other tile would read 0, which says
 * nothing and looks broken. `open` is the denominator the others sit inside.
 */
export interface KeyDateCounts {
  readonly open: number;
  readonly overdue: number;
  readonly dueSoon: number;
  readonly mine: number;
  readonly unassigned: number;
  readonly completed: number;
}

/**
 * The list response. `total` is the WHOLE match, counted before the page —
 * a pager that disagrees with the rows beneath it is worse than no pager
 * (docs/04 §5).
 */
export interface KeyDatesResponse {
  readonly rows: readonly KeyDateRow[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  /** Present only when the request asked for it (`counts=on`). */
  readonly counts?: KeyDateCounts;
  /**
   * Which kinds of record the SCOPE holds — the honest source for the "On"
   * facet (docs/03 §3a: facets come from the rows, never from the taxonomy; an
   * option that filters to nothing is worse than no option).
   *
   * It cannot come from the page, because a server-paged list's page is not the
   * match: an option would appear and vanish as somebody paged. So it is
   * computed over the whole scope in the same aggregate as the counts, the way
   * the history feed already returns `presentActions`.
   */
  readonly kinds?: readonly KeyDateEntityType[];
}
